import { ELEMENT_DRAG_MIME } from "@providers/custom-dnd-provider/constants";
import type { PickerVariable } from "../variable-picker/types";
import {
  buildElementTemplateForVariable,
  type TVariableElementTemplate,
} from "./variable-element-templates";

declare global {
  interface Window {
    __SD_DRAGGABLE_VARIABLE__: { label: string; type: string } | undefined;
  }
}

export type VariableDragPayload = {
  kind: "variable";
  variable: PickerVariable;
  /** Elements-library-compatible fields so the existing drop path can reuse them. */
  id: string;
  json: TVariableElementTemplate["json"];
};

export const buildVariableDragPayload = (
  variable: PickerVariable,
): VariableDragPayload => {
  const template = buildElementTemplateForVariable(variable);
  return { kind: "variable", variable, id: template.id, json: template.json };
};

let ghostEl: HTMLElement | null = null;

/** Off-screen chip used as the drag image (mirrors the picker row look). */
const makeChipGhost = (label: string): HTMLElement => {
  const chip = document.createElement("div");
  chip.textContent = label;
  chip.style.cssText =
    "position:fixed;top:-100px;left:-100px;padding:4px 10px;border-radius:6px;" +
    "border:1px solid #7C3AED;background:#F5F3FF;color:#4C1D95;" +
    "font-size:12px;font-family:inherit;white-space:nowrap;z-index:99999;";
  document.body.appendChild(chip);
  return chip;
};

export const startVariableDrag = (
  e: React.DragEvent | DragEvent,
  variable: PickerVariable,
): void => {
  // Clean up any stale ghost element from a previous drag that didn't end properly
  if (ghostEl) {
    ghostEl.remove();
    ghostEl = null;
  }

  const payload = buildVariableDragPayload(variable);
  // Must match the MIME the canvas drop handler reads (use-dnd.tsx) — it reads
  // ELEMENT_DRAG_MIME and branches on `parsed.kind === "variable"`. (Element
  // drags moved off "text/plain" to this dedicated MIME; the variable source
  // has to follow or drops read empty and no-op.)
  e.dataTransfer?.setData(ELEMENT_DRAG_MIME, JSON.stringify(payload));
  if (e.dataTransfer) e.dataTransfer.effectAllowed = "copy";

  window.__SD_DRAGGABLE_ELEMENT_NAME__ = String(payload.json.settings.name);
  window.__SD_DRAGGABLE_ELEMENT_TYPE__ = "element";
  window.__SD_DRAGGABLE_VARIABLE__ = {
    label: variable.name,
    type: variable.type,
  };
  document.body.classList.add("--editor-dragging");

  ghostEl = makeChipGhost(variable.name);
  e.dataTransfer?.setDragImage(ghostEl, 12, 12);
};

export const endVariableDrag = (): void => {
  window.__SD_DRAGGABLE_ELEMENT_NAME__ = undefined;
  window.__SD_DRAGGABLE_ELEMENT_TYPE__ = undefined;
  window.__SD_DRAGGABLE_VARIABLE__ = undefined;
  document.body.classList.remove("--editor-dragging");
  if (ghostEl) {
    ghostEl.remove();
    ghostEl = null;
  }
};
